import { useState, useEffect, useMemo } from 'react';
import { X, Download } from 'lucide-react';

const InstallPrompt = () => {
    const [showPrompt, setShowPrompt] = useState(false);
    
    // Calculate these values once on mount
    const { isIOS, isStandalone } = useMemo(() => {
        const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                          window.navigator.standalone === true;
        const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        return { isIOS: iOS, isStandalone: standalone };
    }, []);

    useEffect(() => {
        // Don't show if already standalone
        if (isStandalone) return;

        // Check if dismissed recently
        const dismissed = localStorage.getItem('installPromptDismissed');
        const dismissedTime = dismissed ? parseInt(dismissed, 10) : 0;
        const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);

        // Show prompt if not dismissed in last 7 days
        if (daysSinceDismissed > 7) {
            // Wait a bit before showing
            const timer = setTimeout(() => setShowPrompt(true), 5000);
            return () => clearTimeout(timer);
        }
    }, [isStandalone]);

    const handleDismiss = () => {
        localStorage.setItem('installPromptDismissed', Date.now().toString());
        setShowPrompt(false);
    };

    const handleInstall = async () => {
        if (window.deferredPrompt) {
            window.deferredPrompt.prompt();
            const { outcome } = await window.deferredPrompt.userChoice;
            if (outcome === 'accepted') {
                setShowPrompt(false);
            }
            window.deferredPrompt = null;
        }
    };

    if (!showPrompt || isStandalone) return null;

    return (
        <div className="fixed bottom-24 left-4 right-4 z-50 animate-fade-in-up">
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-4">
                <div className="flex items-start gap-3">
                    <img 
                        src="/voilaw-logo.png" 
                        alt="Voilaw" 
                        className="h-12 w-12 object-contain"
                    />
                    <div className="flex-1">
                        <h3 className="font-semibold text-slate-900">Install Voilaw</h3>
                        {isIOS ? (
                            <p className="text-sm text-slate-600 mt-1">
                                Tap the share button, then <strong>&quot;Add to Home Screen&quot;</strong>
                            </p>
                        ) : (
                            <p className="text-sm text-slate-600 mt-1">
                                Add to your home screen for quick access
                            </p>
                        )}
                    </div>
                    <button 
                        onClick={handleDismiss}
                        className="p-1 text-slate-400 hover:text-slate-600"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>
                {!isIOS && (
                    <div className="mt-3 flex gap-2">
                        <button
                            onClick={handleDismiss}
                            className="flex-1 py-2 text-sm text-slate-600 font-medium rounded-xl hover:bg-slate-100"
                        >
                            Not now
                        </button>
                        <button
                            onClick={handleInstall}
                            className="flex-1 py-2 text-sm bg-[#1C6EE7] text-white font-medium rounded-xl flex items-center justify-center gap-2"
                        >
                            <Download className="h-4 w-4" />
                            Install
                        </button>
                    </div>
                )}
                {isIOS && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span className="inline-block w-5 h-5 bg-slate-100 rounded flex items-center justify-center text-lg">↑</span>
                            <span>Tap the share button at the bottom of Safari</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InstallPrompt;
